import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import type {
  BulkGenerateChapterQrDisplay,
  BulkGenerateChapterQrInputs,
  BulkQrMappingDisplay,
  BulkQrMappingInputs,
  QrCodeCreateInputs,
  QrCodeLibraryDetailDisplay,
  QrCodeLibraryDisplay,
  QrCodeMappingDisplay,
  QrCodeResolutionDisplay,
  QrCodeUpdateInputs,
  QrMappingActivityInputs,
  QrMappingInputs,
  QrPrimaryMappingInputs,
  QrSuggestedMappingDisplay,
} from "@/types/qrCodeLibrary";
import type { AppNamesEnum } from "@/enums/appSettings";
import type { PreviewThemeResponse, QrCategoryProps, ValidActivityDisplay } from "@/types/content";
import type { ApiResponse, AzureUploadUrl, PaginatedResponse } from "@/types/global";

/**
 * `/v8/qr-code-library/*` — the QR code library contract.
 *
 * Paths are **relative**: `ApiVerified` sets `baseURL` to `${ApiBaseUrl}/` and
 * `appSettingsStore.ts:106` builds `ApiBaseUrl` as `${host}/v8`, so `qr-code-library/qr-codes`
 * resolves to `{host}/v8/qr-code-library/qr-codes`.
 *
 * Reached only through `storeToRefs(useApiServicesStore())` — never `new QrCodeLibraryApi()`.
 */
export default class QrCodeLibraryApi extends ApiVerified {
  // ─── Resolution (any authenticated role — no content-admin check) ──────────

  /**
   * `api-binding.md` §5.1 — `GET /qr-codes/{qrCode}/resolution`.
   *
   * Accepts both the 16-char and 14-char legacy token forms. The response is **per-student**:
   * `ResolutionSourceId` decides whether the caller navigates straight through, shows the
   * fallback banner, or asks the student to pick a course. Never cache it by token.
   *
   * **The response says WHAT the target is, not WHERE to navigate.** It carries a typed `Target`
   * block rather than a ready-made path, and the client owns routing entirely — see
   * `qrScanResolutionStore.openResolvedTarget` for the seven branches (BR-UI-125). `Target.Url` is
   * resolved fresh on every scan for VIDEO; it is not stored, which is what makes the one-field
   * video re-point possible.
   *
   * 404 `No information available for this QR code`.
   *
   * Two 417s describe a printed code whose target went away AFTER printing — both mean *content
   * gap worth reporting*, not *retry*:
   * - `QR code {code} has no activity linked for course {courseId}`
   * - `QR code {code} points at video library {id}, which is no longer available.`
   *
   * The second is the deliberate one: a library row can be retired long after the code is in a
   * book, and the backend now refuses to hand back a target it knows is dead rather than returning
   * a null URL the viewer would fail on.
   *
   * The old 417 `QR target template needs {token} but the resolved mapping has no {field}` no
   * longer fires — there is no target template.
   */
  async resolveQrCode(qrCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QrCodeResolutionDisplay>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}/resolution`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.2 — `GET /short-codes/{qrShortCode}/resolution`.
   *
   * The manual-entry fallback when the camera cannot read the code. Identical response shape to
   * §5.1. The short code moved from a query param on the retired `getQrCodeDetailsByShortCode` to
   * a **path** param here, and it is typed by hand — hence the encode.
   *
   * **Two formats are live and always will be.** New short codes are 6 characters (`7H2K9Q`);
   * codes already printed in books keep the legacy 10-character form (`VCK063544A`) forever and
   * are never regenerated. Do not length-check, regex or parse a code — the server does an exact
   * string lookup and is the only authority (BR-UI-120).
   *
   * 404 `No information available for this QR short code`; 417 as §5.1.
   */
  async resolveQrShortCode(qrShortCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QrCodeResolutionDisplay>(
        `qr-code-library/short-codes/${encodeURIComponent(qrShortCode)}/resolution`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.3 — `POST /qr-codes/bulk-resolution`. 1–200 tokens.
   *
   * Tokens that do not resolve are **omitted** from the array — the response can be shorter than
   * the request and there is no per-token status, so match results back by `QrCode`. Enforce the
   * 1–200 bound client-side (`QR_BULK_MAX_CODES`); the 422 here is a raw Pydantic array.
   *
   * No screen consumes this yet (OQ-14) — spec'd so the migration off `getAllQrCodeDetails` is
   * complete.
   */
  async resolveQrCodesBulk(qrCodes: Array<string>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<QrCodeResolutionDisplay>>(
        `qr-code-library/qr-codes/bulk-resolution`,
        { QrCodes: qrCodes },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Library browse + detail (content admin) ───────────────────────────────

  /**
   * `api-binding.md` §5.5 — `GET /qr-codes`.
   *
   * `pageNumber` is 1-based. `pageSize` is capped server-side at 200 — clamp to
   * `QR_BROWSE_MAX_PAGE_SIZE` in the store or the server 417s. `subjectChapterId` means
   * "in this chapter **or any topic/concept inside it**", not an exact match, so the filter is
   * labelled "In chapter". `searchText` matches the short code or the title, case-insensitively.
   *
   * Replaces `getQrCodesForCategory`, `getQrCodesForChapter` and `getQrCodesForContent`.
   * Empty `items: []` with `count: 0` is a routine state, not an error.
   */
  async getQrCodes(
    pageNumber: number,
    pageSize: number,
    qrCategoryId: number | null,
    versionNo: number | null,
    courseId: number | null,
    subjectChapterId: number | null,
    searchText: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<QrCodeLibraryDisplay>>(
        `qr-code-library/qr-codes`,
        {
          params: {
            pageNumber: pageNumber,
            pageSize: pageSize,
            qrCategoryId: qrCategoryId,
            versionNo: versionNo,
            courseId: courseId,
            subjectChapterId: subjectChapterId,
            searchText: searchText,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.4 — `GET /qr-codes/{qrCode}`.
   *
   * One call feeds the whole QR Detail screen — identity, `Design` and up to 25 `Mappings`
   * arrive inline, so there is no follow-up fetch. `Mappings: []` is structurally valid and is a
   * **problem** state, not an empty state.
   *
   * 404 `No information available for this QR code` (retired or never existed); 404
   * `QR code {code} has no linked design` is a data defect, not user error. A 401 here means
   * "not a content admin" — check the detail string, it is not a token expiry.
   */
  async getQrCodeDetail(qrCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QrCodeLibraryDetailDisplay>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Mappings — every mutation returns the FULL active mapping list ────────
  //
  // The response body **is** the new source of truth for §5.15 / §5.16 / §5.17 / §5.18. Course,
  // chapter and activity are derived server-side, so the resulting row is not predictable: assign
  // the body straight to the mapping list — never refetch, patch by hand, or update optimistically.

  /**
   * `api-binding.md` §5.6 — `GET /qr-codes/{qrCode}/suggested-mappings`.
   *
   * Candidate courses that teach the same content. Deliberately **unbounded** — never
   * client-truncate, a manager needs to see every course that was held back. Slow on a widely
   * taught concept. `IsAlreadyMapped` candidates stay in the list, greyed; `[]` means the content
   * was never copied into another course, which is a content-ops gap rather than an error.
   *
   * 417 when the category is not course-scoped — gate the entry point on
   * `QR_NOT_COURSE_SCOPED_CATEGORY_IDS` so this never fires.
   */
  async getSuggestedMappings(
    qrCode: string,
    academicSession: number | null,
    onlyUnmapped: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<QrSuggestedMappingDisplay>>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}/suggested-mappings`,
        { params: { academicSession: academicSession, onlyUnmapped: onlyUnmapped } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.15 — `POST /qr-codes/{qrCode}/mappings`.
   *
   * Sends **only** `ContentTypeId` + `ContentId`, taken verbatim from the candidate row.
   * `CourseId`, `SubjectChapterId` and `ActivityId` are derived server-side and ignored if sent.
   * Returns the full active set after the write.
   *
   * 417 on an already-mapped content, a course conflict, inactive content, or a content-level
   * mismatch; 404 when the content no longer exists.
   */
  async addQrMapping(qrCode: string, inputs: QrMappingInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<QrCodeMappingDisplay>>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}/mappings`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.16 — `DELETE /qr-codes/{qrCode}/mappings/{contentTypeId}/{contentId}`.
   *
   * Three path params, **no body**. Returns 200 with the remaining mappings, unlike §5.13's
   * bodyless 204. Removing the **last** mapping is permitted and lands the screen in the
   * `Mappings: []` problem state.
   *
   * 417 when the target is the primary and others remain — disable the remove action on the
   * primary row while `Mappings.length > 1` so this never fires.
   */
  async removeQrMapping(
    qrCode: string,
    contentTypeId: number,
    contentId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<Array<QrCodeMappingDisplay>>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}/mappings/${contentTypeId}/${contentId}`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.17 — `PUT /qr-codes/{qrCode}/primary-mapping`.
   *
   * Sets the fallback course — where a student in none of the mapped courses lands. Returns all
   * mappings with **exactly one** `IsPrimary: true`, so the column renders as a radio group,
   * never checkboxes.
   */
  async setPrimaryQrMapping(qrCode: string, inputs: QrPrimaryMappingInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<Array<QrCodeMappingDisplay>>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}/primary-mapping`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.7 — `GET /qr-codes/{qrCode}/valid-activities`.
   *
   * `contentId` is **required** and is **per-mapping**: each course holds its own activity copy,
   * so the candidate list differs per mapping row. Never cache one list across rows. `[]` is a
   * common, meaningful outcome — "no matching activity in this course".
   *
   * 417 when the category does not link to an activity; 404 when the mapping was removed.
   */
  async getValidActivities(qrCode: string, contentId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ValidActivityDisplay>>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}/valid-activities`,
        { params: { contentId: contentId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.18 — `PUT /qr-codes/{qrCode}/mappings/activity`.
   *
   * `ActivityId` must appear in the §5.7 list for that mapping's content, and is non-nullable —
   * there is no unlink endpoint. Returns the full mapping list.
   *
   * 417 when the activity is not a valid match (the §5.7 list went stale — re-fetch it).
   */
  async updateQrMappingActivity(
    qrCode: string,
    inputs: QrMappingActivityInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<Array<QrCodeMappingDisplay>>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}/mappings/activity`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Authoring (content admin) ─────────────────────────────────────────────

  /**
   * `api-binding.md` §5.11 — `POST /qr-codes`.
   *
   * **Atomic**: writes the library row, the design and the first mapping in one call, so there is
   * no two-step wizard holding an unsaved payload. Returns HTTP **200, not 201**.
   *
   * Check `IsExistingQrCode` on the response — `true` means nothing was created and the caller is
   * looking at the existing record, which is not a success. It answers "an **active QR already
   * maps this content** at this category / subtype / edition", not "this exact token existed".
   * Slow: renders and uploads the card images. A 500 means the image failed its scannability check
   * and **nothing was saved**, so a retry is safe. `ContentTypeId` is derived from
   * `/category-props`, never user-picked.
   *
   * 417s worth knowing about, all surfaced by the caller — this layer adds no client-side gate:
   * - `HOMEWORK` category is rejected outright; those codes come from the homework OMR generator.
   *   The Mint dropdown is gated on `QR_MINTABLE_CATEGORY_IDS` so this cannot fire (BR-UI-122).
   * - A second question link is rejected; offer "replace the linked question" (BR-UI-123).
   * - The old `QR code {code} was retired on {date}` 417 **no longer fires** — retiring a code no
   *   longer blocks minting a new one for the same content. See `retireQrCode`.
   *
   * **The target is two typed fields and exactly one is required** — which one is decided by the
   * category's target type, and three types require neither (BR-UI-129). Drive it off
   * `categoryProps.targetTypeId`, never a hardcoded table:
   * - VIDEO → `TargetLibraryId` (a VideoLibraryId) · PRESENTATION → `TargetLibraryId` (a
   *   LectureLibraryId) · PDF/IMAGE → `TargetAssetUrl` · ACTIVITY/PRACTICE/HINTS → **both null**
   *
   * Five target 417s replace the three removed target-template ones:
   * - `{field} must not be set for a {TYPE} target` / `{field} is required for a {TYPE} target.` —
   *   inline on the field; usually means the form did not follow `targetTypeId`, i.e. a UI defect
   * - `Video library {id} not found or inactive.` / `Lecture library {id} not found, inactive, or
   *   has no linked design.` — the picker went stale; refresh it
   * - `Lecture library {id} has no presentation pages — …` — **a content gap, not a form error**:
   *   the author must add slides first, and re-submitting the same lecture cannot help. Matched by
   *   `isQrContentGapError` so it never lands in a field's validation slot (REQ-R2-14).
   */
  async createQrCode(inputs: QrCodeCreateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<QrCodeLibraryDetailDisplay>(
        `qr-code-library/qr-codes`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.12 — `PATCH /qr-codes/{qrCode}`.
   *
   * Editable: title, description, additional info and the typed target. Send `TargetLibraryId`
   * OR `TargetAssetUrl`, never both; an omitted field leaves the stored value unchanged, so never
   * send `null` to mean "unchanged". Token, short code, category and print version are write-once:
   * they are physically committed to paper.
   *
   * Returns the full detail payload (the old `updateQrCodeContent` returned a `boolean`) —
   * re-render from it.
   *
   * ⚠ TWO DIFFERENT EDITS SHARE THIS METHOD AND THEY ARE NOT EQUALLY DANGEROUS.
   * - Card-text or `TargetAssetUrl` edits RE-RENDER the card images: slow, and the printed artwork
   *   changes. That is the case the destructive replace-target confirm exists for.
   * - A VIDEO RE-POINT — `{ TargetLibraryId }` alone — does NOT re-render the card images. The
   *   printed artwork is untouched, which is correct, because the card is already in a book. It is
   *   a SAFE one-field edit that reaches every printed copy instantly, and it must NOT carry the
   *   replace-target warning (BR-UI-131).
   *
   * Rejects with the same five target `417`s as create; the three target-template `417`s no longer
   * fire.
   */
  async updateQrCodeDesign(qrCode: string, inputs: QrCodeUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.patch<QrCodeLibraryDetailDisplay>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.13 — `DELETE /qr-codes/{qrCode}`.
   *
   * Returns **204 No Content with no body** — the only bodyless response in this service, and the
   * first in the codebase. `handleApiResponse` reports `isSuccess: true` regardless of the body
   * (verified under qrlib-3-8 and pinned by `tests/unit/methods/api.spec.ts`), so a successful
   * retire is not misreported as a failure.
   *
   * **Irreversible, and immediate.** Soft delete cascading to every mapping and the design. The
   * token is dead forever and the code printed in existing books stops working the instant this
   * resolves.
   *
   * What is **no longer** true: the content is not blocked. Tokens are now random rather than
   * derived, so a genuinely new code can be minted for the same chapter afterwards — §5.11 no
   * longer returns `QR code {code} was retired on {date}`. See
   * `qrCodeLibraryStore.replaceQrCode()`, which sequences DELETE then POST in that order.
   *
   * Only call behind a destructive confirmation (BR-UI-025).
   */
  async retireQrCode(qrCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<void>(
        `qr-code-library/qr-codes/${encodeURIComponent(qrCode)}`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Theme + assets ────────────────────────────────────────────────────────

  /**
   * `api-binding.md` §5.8 — `GET /category-props`.
   *
   * Static metadata keyed by category id, readable by **any authenticated role** — the one
   * admin-adjacent endpoint that is not gated. Cache it for the session.
   *
   * Supplies `contentTypeId`, the authoritative mapping level, which is why the category's
   * `contentType` string is deprecated: it reads "Topic" where the id is a `ChapterTopicId`.
   * Also supplies `primaryColor` / `backgroundColor` as RGB tuples. It does **not** supply
   * category labels — those live in `QR_CATEGORY_LABELS`.
   */
  async getQrCategoryProps(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Record<number, QrCategoryProps>>(
        `qr-code-library/category-props`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.9 — `GET /theme-preview`.
   *
   * Renders a throwaway card. **Nothing is persisted**, and the returned `qrImageUrls` are base64
   * data rather than blob URLs. Debounce against the title field. A 500 means the render failed
   * its scannability check.
   *
   * `activitySubtypeId` is accepted by the endpoint but **has no effect**, so it is omitted here.
   */
  async getThemePreview(
    qrCategoryId: number,
    appName: AppNamesEnum,
    qrTitle: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PreviewThemeResponse>(
        `qr-code-library/theme-preview`,
        { params: { qrCategoryId: qrCategoryId, appName: appName, qrTitle: qrTitle } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.10 — `GET /target-upload-url`.
   *
   * Returns `{ url, sasToken }` for a direct-to-Azure `PUT`; the file never passes through the
   * API. Feed it to the existing `SasUploader` component.
   *
   * **Warn before calling.** Requesting the URL already stamps who did it and when onto the
   * design, and the blob is keyed by the token — so replacing the file changes where every
   * already-printed copy points, and there is no way to update the books.
   */
  async getTargetUploadUrl(qrCode: string, contentType: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `qr-code-library/target-upload-url`,
        { params: { qrCode: qrCode, contentType: contentType } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Bulk operations ───────────────────────────────────────────────────────

  /**
   * `api-binding.md` §5.14 — `POST /qr-codes/bulk-generate`.
   *
   * The slowest operation in the feature — it renders 8 card images per code in the bundle.
   * Idempotent on the token, so a re-run after a partial failure is safe and `ExistingQrCodes`
   * is the normal outcome of that.
   *
   * Returns three arrays: created, already-existing, and topics skipped for sitting below the
   * concept-quiz question floor. A skip is **not** a failure, but the content team needs to see
   * it — present all three.
   */
  async bulkGenerateChapterQrCodes(inputs: BulkGenerateChapterQrInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<BulkGenerateChapterQrDisplay>(
        `qr-code-library/qr-codes/bulk-generate`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * `api-binding.md` §5.19 — `POST /qr-codes/bulk-map`.
   *
   * Preview with `IsPreview: true` (nothing is written), then commit the **identical** body with
   * it `false`. The preview runs the same code path as the commit, so what it shows is exactly
   * what will happen; `CreatedCount` is always `0` on a preview.
   *
   * The 1..`QR_BULK_MAX_CODES` bound is enforced in the store, not here — the 422 this endpoint
   * returns is a raw Pydantic array rather than a friendly message.
   */
  async bulkMapQrCodes(inputs: BulkQrMappingInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<BulkQrMappingDisplay>(
        `qr-code-library/qr-codes/bulk-map`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
